"use client";

import React from "react";
import Quote from "@/assets/Quote";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { Button } from "../atoms/Button";
import QuoteCard from "./QuoteCard";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

const OnlineMasterClassTitleCard = ({
  name,
  designation,
  icon1,
  icon2,
  nameTextColor,
  designationTextColor,
  variant,
  info1Label,
  info1Value,
  info2Label,
  info2Value,
  infoLabelColor,
  infoValueColor,
  infoDividerColor,
}: {
  name: string;
  designation: string;
  icon1: string;
  icon2: string;
  nameTextColor?: string;
  designationTextColor?: string;
  variant?: string;
  info1Label?: string;
  info1Value?: string;
  info2Label?: string;
  info2Value?: string;
  infoLabelColor?: string;
  infoValueColor?: string;
  infoDividerColor?: string;
}) => {
  const isV2 = variant === "variant_2";
  const showInfo = isV2 && (info1Value || info2Value);

  const infoPair = (label?: string, value?: string) =>
    value ? (
      <div className="flex flex-col gap-1 xs:items-center sm:items-start">
        {label && (
          <span
            className="font-medium text-[12px] tracking-[0.12px] xs:text-center sm:text-left"
            style={{ color: infoLabelColor || "#cf6384" }}
          >
            {label}
          </span>
        )}
        <span
          className="font-normal text-[18px] leading-normal xs:text-center sm:text-left"
          style={{ color: infoValueColor || "#f8f9fa" }}
        >
          {value}
        </span>
      </div>
    ) : null;

  return (
    <div className={cn("flex flex-col", "xs:gap-6", "gap-[14px]")}>
      <div className={cn("flex flex-col", "xs:gap-[6px]")}>
        <div
          className={cn(
            "text-center line-clamp-1",
            "sm:text-left sm:w-[180px] sm:line-clamp-1 md:w-[230px] lg:w-[260px] xl:w-[370px]",
            isV2
              ? "font-poppins font-normal leading-normal text-[24px] md:text-[28px] lg:text-[32px]"
              : cn(
                  "font-wf-visual-sans leading-130 type-h4",
                  "max-[799px]:text-[20px] max-[799px]:font-semibold max-[799px]:tracking-[-0.2px]",
                  "min-[800px]:text-heading-6-semibold",
                  "sm:text-heading-6-semibold",
                  "md:text-heading-5-semibold",
                  "lg:text-heading-4-semibold",
                  "xl:text-new-heading-34-semibold",
                ),
          )}
          style={{ color: nameTextColor || "#000000" }}
        >
          {name}
        </div>
        <div
          className={cn(
            "flex flex-col w-fit gap-2",
            "xs:justify-center xs:items-center xs:text-center xs:w-full",
            "sm:justify-start sm:items-start",
          )}
        >
          <div
            className={cn(
              "flex justify-center items-center line-clamp-1",
              "sm:w-[230px] sm:text-left sm:line-clamp-1 md:w-[260px] xl:w-[370px]",
              isV2
                ? "font-poppins font-normal leading-normal text-[14px] min-[800px]:text-[18px]"
                : cn(
                    "font-wf-visual-sans leading-140 type-body-sm",
                    "max-[799px]:text-[14px] max-[799px]:font-normal max-[799px]:-tracking-012",
                    "min-[800px]:text-heading-9-regular min-[800px]:-tracking-014",
                    "sm:text-heading-9-regular",
                    "md:text-body-2-regular",
                    "lg:text-heading-7-regular",
                    "xl:text-heading-6-regular xl:-tracking-014",
                  ),
            )}
            style={{ color: designationTextColor || "#666666" }}
          >
            {designation}
          </div>
          <div
            className={cn(
              "bg-primary-3",
              "xs:h-[1.6px] xs:w-[40%]",
              "sm:w-[60%] sm:h-[1.6px]",
            )}
          />
        </div>
      </div>

      <div className={cn("flex", "xs:justify-center", "sm:justify-start")}>
        {[icon1, icon2].map(
          (item, index) =>
            item && (
              <div
                className={cn("flex bg-white pl-[14px] py-[2px]", {
                  "rounded-tl-lg rounded-bl-lg": index === 0,
                  "rounded-tr-lg rounded-br-lg": index === 1,
                })}
                key={index}
              >
                <Image
                  src={item}
                  alt={item}
                  width={100}
                  height={100}
                  className={cn(
                    "pr-[14px]",
                    "sm:w-[62px] sm:h-[24px]",
                    "md:w-[74px] md:h-[28px]",
                    "lg:w-[94px] lg:h-[36px]",
                    "xl:w-[94px] xl:h-[36px]",
                  )}
                />
                {index !== 1 && (
                  <div className="w-[2px] h-full rounded-[30px] bg-primary-3" />
                )}
              </div>
            ),
        )}
      </div>

      {showInfo && (
        <div
          className={cn(
            "flex items-stretch gap-[28px] font-poppins",
            "xs:justify-center xs:w-full",
            "sm:justify-start sm:w-full",
          )}
        >
          {infoPair(info1Label, info1Value)}
          {info1Value && info2Value && (
            <div
              className="w-px self-stretch shrink-0"
              style={{
                backgroundColor: infoDividerColor || "rgba(255,255,255,0.3)",
              }}
            />
          )}
          {infoPair(info2Label, info2Value)}
        </div>
      )}
    </div>
  );
};

const OnlineMasterClassCard = ({
  item,
  variant,
  buttonText,
  buttonBackgroundColor,
  buttonTextColor,
  buttonIcon,
  buttonHoverIcon,
  onButtonClick,
  showButton = true,
  showQuoteIcon = true,
  quoteIcon = "",
  quoteIconAlt = "",
}: {
  item: any;
  variant?: string;
  buttonText?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  buttonIcon?: string;
  buttonHoverIcon?: string;
  onButtonClick?: () => void;
  showButton?: boolean;
  showQuoteIcon?: boolean;
  quoteIcon?: string;
  quoteIconAlt?: string;
}) => {
  const isMobile = useIsMobile(800);
  const isV2 = variant === "variant_2";
  const [isHovered, setIsHovered] = React.useState(false);
  const dispatch = useDispatch();
  const {
    meta_name,
    meta_mobile_image,
    meta_desktop_image,
    meta_designation,
    meta_description,
    meta_alt_image,
    meta_icon1,
    meta_icon2,
    meta_name_text_color,
    meta_designation_text_color,
    meta_description_text_color,
    meta_description_background_color,
    meta_info_1_label,
    meta_info_1_value,
    meta_info_2_label,
    meta_info_2_value,
    meta_info_label_color,
    meta_info_value_color,
    meta_info_divider_color,
  } = item;

  if (isMobile) {
    return (
      <div className="flex flex-col items-center justify-center gap-6">
        <OnlineMasterClassTitleCard
          name={meta_name}
          designation={meta_designation}
          icon1={meta_icon1}
          icon2={meta_icon2}
          nameTextColor={meta_name_text_color}
          designationTextColor={meta_designation_text_color}
          variant={variant}
          info1Label={meta_info_1_label}
          info1Value={meta_info_1_value}
          info2Label={meta_info_2_label}
          info2Value={meta_info_2_value}
          infoLabelColor={meta_info_label_color}
          infoValueColor={meta_info_value_color}
          infoDividerColor={meta_info_divider_color}
        />
        <div className="flex flex-col relative items-center justify-center ">
          <div className={cn("xs:w-[248px] xs:h-[248px]")} />

          {/* <Quote
            fill="#0C364E59"
            className={cn(
              "absolute top-30 left-1/6 -translate-x-1/6 z-10",
              "xs:size-[130px]"
            )}
            styles={{ mixBlendMode: "overlay" }}
          /> */}
          <QuoteCard
            description={meta_description}
            descriptionTextColor={meta_description_text_color}
            descriptionBackgroundColor={meta_description_background_color}
            variant={variant}
            showQuoteIcon={showQuoteIcon}
            quoteIcon={quoteIcon}
            quoteIconAlt={quoteIconAlt}
          />
        </div>
        {showButton ? (
          <button
            onClick={() =>
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                }),
              )
            }
            className="w-fit flex gap-2 py-[6px] px-[10px] rounded-[4px] "
            style={{
              backgroundColor: buttonBackgroundColor || "#BE2330",
              color: buttonTextColor || "#ffffff",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <span
              className={cn(
                "font-wf-visual-sans text-[14px] font-semibold leading-140 -tracking-014",
              )}
            >
              {buttonText || "Enroll Now"}
            </span>
            {buttonIcon && (
              <div className={cn("relative", " xs:size-6 ")}>
                <Image
                  src={
                    isHovered && buttonHoverIcon ? buttonHoverIcon : buttonIcon
                  }
                  alt="Button Icon"
                  fill
                  className="transition-opacity"
                />
              </div>
            )}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <div
        className={cn(
          "relative flex justify-between",
          "sm:w-[762px] sm:pb-[76px]",
          "md:w-[762px]",
          "lg:w-[954px]",
          "xl:w-[1356px] xl:pb-[176px]",
          // Variant 2: vertically center the quote box against the profile column.
          // Bottom padding is kept for both variants so the card height/spacing is unchanged.
          isV2 && "items-center",
        )}
      >
        <OnlineMasterClassTitleCard
          name={meta_name}
          designation={meta_designation}
          icon1={meta_icon1}
          icon2={meta_icon2}
          nameTextColor={meta_name_text_color}
          designationTextColor={meta_designation_text_color}
          variant={variant}
          info1Label={meta_info_1_label}
          info1Value={meta_info_1_value}
          info2Label={meta_info_2_label}
          info2Value={meta_info_2_value}
          infoLabelColor={meta_info_label_color}
          infoValueColor={meta_info_value_color}
          infoDividerColor={meta_info_divider_color}
        />
        {/* {meta_desktop_image && (
          <Image
            src={meta_desktop_image}
            alt={meta_alt_image}
            width={100}
            height={100}
            className={cn(
              "absolute bottom-0 left-1/3 -translate-x-1/3 -z-10",
              "sm:w-[272px] sm:h-[260px]",
              "md:w-[272px] md:h-[260px]",
              "lg:w-[272px] lg:h-[260px]",
              "xl:w-[440px] xl:h-[420px]"
            )}
          />
        )} */}

        {/* <Quote
          fill="#0C364E59"
          className={cn(
            "sm:w-[150px] sm:h-[124px] absolute bottom-1 left-1/6 -translate-x-1/6 -z-10",
            "xl:w-[242px] xl:h-[200px]"
          )}
          styles={{ mixBlendMode: "overlay" }}
        /> */}

        <div className="flex flex-col gap-4">
          <QuoteCard
            description={meta_description}
            descriptionTextColor={meta_description_text_color}
            descriptionBackgroundColor={meta_description_background_color}
            variant={variant}
            showQuoteIcon={showQuoteIcon}
            quoteIcon={quoteIcon}
            quoteIconAlt={quoteIconAlt}
          />
          {showButton ? (
          <button
            onClick={() =>
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                }),
              )
            }
            className={cn(
              "w-fit flex gap-[10px] items-center justify-center cursor-pointer",
              "sm:px-[18px] sm:py-2 sm:rounded-[8px] ",
              "md:gap-2 md:px-[18px] md:py-2 md:rounded-[8px] ",
              "lg:gap-[10px] lg:px-[18px] lg:py-2 lg:rounded-[8px]",
              "xl:gap-[10px] xl:px-[18px] xl:py-2 xl:rounded-[8px]",
            )}
            style={{
              backgroundColor: buttonBackgroundColor || "#BE2330",
              color: buttonTextColor || "#ffffff",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <span
              className={cn(
                "sm:text-heading-9-semibold",
                "md:text-heading-9-semibold",
                "xl:text-heading-7-semibold",
              )}
            >
              {buttonText}
            </span>
            {buttonIcon && (
              <div
                className={cn(
                  "relative",
                  "sm:size-[26px]",
                  "md:size-6",
                  "lg:size-[26px]",
                  "xl:size-[26px]",
                )}
              >
                <Image
                  src={
                    isHovered && buttonHoverIcon ? buttonHoverIcon : buttonIcon
                  }
                  alt="Button Icon"
                  fill
                  className="transition-opacity"
                />
              </div>
            )}
          </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OnlineMasterClassCard;
